import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, CheckCircle, Spinner } from '@phosphor-icons/react';
import CountryCodeSelect from '@/components/CountryCodeSelect';
import { ERS_LABEL } from '@/config/legal';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;
const accent = '#5B8C7B';
const PROGRAMS = [
  { value: 'Super Pré Mamãs', label: 'Pré-conceção (Super Pré Mamãs)' },
  { value: 'Super Barriguinhas', label: 'Gravidez (Super Barriguinhas)' },
  { value: 'Super Mamãs', label: 'Pós-parto (Super Mamãs)' },
  { value: 'Super Bebés', label: 'Introdução Alimentar (Super Bebés)' },
  { value: 'Nutrição Pediátrica', label: 'Nutrição Pediátrica' },
  { value: 'Geral', label: 'Geral' },
];
const DAYS = [
  { v: 'segunda', l: 'Segunda' }, { v: 'terca', l: 'Terça' }, { v: 'quarta', l: 'Quarta' },
  { v: 'quinta', l: 'Quinta' }, { v: 'sexta', l: 'Sexta' }, { v: 'sabado', l: 'Sábado' },
];
const PERIODS = [
  { v: 'manha', l: 'Manhã' }, { v: 'tarde', l: 'Tarde' }, { v: 'indiferente', l: 'Indiferente' },
];

export default function WaitlistPublicPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', country_code: '+351', program: '', message: '', website: '', available_days: [], preferred_period: 'indiferente', dum: '', delivery_date: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [programDescriptions, setProgramDescriptions] = useState({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [consentVersion, setConsentVersion] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/site-content`);
        setProgramDescriptions(data.program_descriptions || {});
        setConsentVersion(`${data.terms_and_conditions_updated_at || ''}|${data.privacy_policy_updated_at || ''}`);
      }
      catch {}
    })();
  }, []);

  const toggleDay = (d) => setForm(f => ({ ...f, available_days: f.available_days.includes(d) ? f.available_days.filter(x => x !== d) : [...f.available_days, d] }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.program) {
      setError('Preenche o nome, email, telefone e o programa de interesse.');
      return;
    }
    if (!privacyConsent) { setError('Tens de aceitar os Termos de Serviço e a Política de Privacidade.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/waitlist`, { ...form, privacy_consent: privacyConsent, consent_version: consentVersion });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível submeter. Tenta novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="public-waitlist-page">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#2E3A33]">
            <Users size={26} weight="duotone" style={{ color: accent }} />
            <h1 className="font-heading font-semibold text-2xl">Lista de espera</h1>
          </div>
          <p className="text-sm text-[#7A8278] mt-1 break-words">Ana Magalhães Pereira · Nutrição materno-infantil · {ERS_LABEL}</p>
        </div>

        {done ? (
          <div className="bg-white rounded-3xl border border-[#E8E3DB] p-8 text-center" data-testid="waitlist-success">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-green-600" />
            <h2 className="font-heading text-xl text-[#2E3A33] mt-3">Estás na lista! 💛</h2>
            <p className="text-sm text-[#7A8278] mt-2 break-words">Obrigada pelo teu interesse no programa <strong>{form.program}</strong>. Entraremos em contacto assim que abrir uma vaga.</p>
            <p className="text-xs text-[#A8AFA3] mt-4 break-words">Enviámos uma confirmação para o teu email.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E8E3DB] p-6 space-y-4" data-testid="waitlist-public-form">
            <p className="text-sm text-[#7A8278] break-words">As vagas são limitadas para garantir um acompanhamento de qualidade. Deixa os teus dados para teres prioridade quando abrir uma nova vaga.</p>

            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />

            <div>
              <label className="text-xs text-[#7A8278] mb-1 block">Nome completo *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#7A8278] mb-1 block">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-email" />
              </div>
              <div>
                <label className="text-xs text-[#7A8278] mb-1 block">Telefone *</label>
                <div className="flex gap-1"><CountryCodeSelect value={form.country_code || '+351'} onChange={v => setForm({ ...form, country_code: v })} testId="wl-country-code" /><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="flex-1 border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-phone" /></div>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#7A8278] mb-1 block">Programa de interesse *</label>
              <select value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-program">
                <option value="">Seleciona o programa…</option>
                {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              {programDescriptions[form.program] && (
                <p className="text-[11px] text-[#7A8278] mt-1.5" data-testid="wl-program-desc">{programDescriptions[form.program]}</p>
              )}
            </div>
            {form.program === 'Super Barriguinhas' && (
              <div data-testid="wl-dum-field">
                <label className="text-xs text-[#7A8278] mb-1 block">Data da última menstruação (DUM) · opcional</label>
                <DateField value={form.dum} onChange={e => setForm({ ...form, dum: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-dum" />
              </div>
            )}
            {form.program === 'Super Mamãs' && (
              <div data-testid="wl-delivery-field">
                <label className="text-xs text-[#7A8278] mb-1 block">Data do parto · opcional</label>
                <DateField value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-delivery-date" />
              </div>
            )}
            <div>
              <label className="text-xs text-[#7A8278] mb-2 block">Dias disponíveis (opcional)</label>
              <div className="flex flex-wrap gap-2" data-testid="wl-days">
                {DAYS.map(d => (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => toggleDay(d.v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${form.available_days.includes(d.v) ? 'text-white' : 'bg-[#FAF8F4] border border-[#E8E3DB] text-[#7A8278]'}`}
                    style={form.available_days.includes(d.v) ? { backgroundColor: accent } : {}}
                    data-testid={`wl-day-${d.v}`}
                  >{d.l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#7A8278] mb-1 block">Período preferido</label>
              <select value={form.preferred_period} onChange={e => setForm({ ...form, preferred_period: e.target.value })} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-period">
                {PERIODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#7A8278] mb-1 block">Mensagem (opcional)</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className="w-full border border-[#E8E3DB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B8C7B]/30" data-testid="wl-message" />
            </div>

            {error && <p className="text-sm text-red-600 break-words" data-testid="wl-error">{error}</p>}

            <label className="flex items-start gap-2 text-[12px] text-[#2E3A33] cursor-pointer" data-testid="wl-privacy-label">
              <input type="checkbox" checked={privacyConsent} onChange={e => setPrivacyConsent(e.target.checked)} className="mt-0.5" data-testid="wl-privacy-consent" />
              <span>Li e aceito os <a href="/termos-e-condicoes" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="wl-terms-link">Termos de Serviço</a> e a <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="wl-privacy-link">Política de Privacidade</a>.</span>
            </label>

            <button type="submit" disabled={submitting || !privacyConsent} className="w-full text-white rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: accent }} data-testid="wl-submit">
              {submitting ? <><Spinner size={18} className="animate-spin" /> A submeter…</> : 'Juntar à lista de espera'}
            </button>
          </form>
        )}

        <div className="text-center mt-6">
          <p className="text-[11px] text-[#A8AFA3]">Ana Magalhães Pereira · Nutricionista (Cédula 2758N) · {ERS_LABEL} · ana@nutrianapereira.pt</p>
        </div>
      </div>
    </div>
  );
}
